/**
 * FilterTabs — Horizontal scrollable pill-shaped filter tabs
 *
 * Used for: status filters (pending/accepted/rejected), category filters
 * From: IncomingOffersScreen, InventoryReportsScreen filter tabs
 */
import type { ReactNode } from 'react'
import { styled, XStack, Text as TamaguiText, ScrollView, type GetProps } from 'tamagui'

const FilterTabFrame = styled(XStack, {
  name: 'FilterTab',
  role: 'button',
  cursor: 'pointer',
  userSelect: 'none',
  ai: 'center',
  jc: 'center',
  px: '$md',
  py: '$sm',
  br: '$full',
  bg: '$backgroundSecondary',
  animation: 'quick',
  animateOnly: ['opacity', 'transform'],
  pressStyle: { opacity: 0.8, scale: 0.97 },
  hoverStyle: { opacity: 0.85 },
  hoverStyle: { opacity: 0.9 },

  variants: {
    active: {
      true: {
        bg: '$colorPrimary',
      },
    },
  } as const,
})

const FilterTabLabel = styled(TamaguiText, {
  name: 'FilterTabLabel',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '500',
  color: '$colorSecondary',

  variants: {
    active: {
      true: {
        color: '$colorInverse',
        fontWeight: '600',
      },
    },
  } as const,
})

interface FilterTabOption {
  value: string
  label: string
}

type FilterTabsFrameProps = GetProps<typeof XStack>

interface FilterTabsProps extends FilterTabsFrameProps {
  options: FilterTabOption[]
  value: string
  onValueChange: (value: string) => void
  /** Render in a horizontal ScrollView for many options */
  scrollable?: boolean
}

export const FilterTabs = XStack.styleable<FilterTabsProps>(
  ({ options, value, onValueChange, scrollable = false, ...props }, ref) => {
    const content = (
      <XStack ref={scrollable ? undefined : ref} gap="$xs" {...(scrollable ? {} : props)}>
        {options.map((opt) => (
          <FilterTabFrame
            key={opt.value}
            active={value === opt.value}
            onPress={() => onValueChange(opt.value)}
          >
            <FilterTabLabel active={value === opt.value}>
              {opt.label}
            </FilterTabLabel>
          </FilterTabFrame>
        ))}
      </XStack>
    )

    if (scrollable) {
      return (
        <ScrollView
          ref={ref as any}
          horizontal
          showsHorizontalScrollIndicator={false}
        >
          {content}
        </ScrollView>
      )
    }

    return content
  },
)
